import { motion, type Transition, useAnimation, useInView } from 'framer-motion';
import { memo, useEffect, useId, useRef, useState } from 'react';

const NUMBERS = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 0, 1, 2, 3, 4, 5, 6, 7, 8, 9];

interface Props {
  className?: string;
  animateToNumber: string;
  fontStyle?: React.CSSProperties;
  transitions?: (index: number) => Transition;
  includeComma?: boolean;
  locale?: string;
}

const AnimatedNumber = ({ className, animateToNumber, fontStyle, transitions, includeComma, locale }: Props) => {
  const ref = useRef(null);
  const id = useId();
  const isInView = useInView(ref, { once: true });

  const controls = useAnimation();
  const animateToNumbersArr = Array.from(animateToNumber, Number).map((x, idx) =>
    Number.isNaN(x) ? animateToNumber[idx] : x
  );

  const [numberHeight, setNumberHeight] = useState(0);
  const [numberWidth, setNumberWidth] = useState(0);

  const numberDivRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const rect = numberDivRef.current?.getClientRects()?.[0];
    if (rect) {
      setNumberHeight(rect.height);
      setNumberWidth(rect.width);
    }
  }, []);

  useEffect(() => {
    if (isInView) {
      controls.start('visible');
    }
    //todo:v2-changes
  }, [isInView, animateToNumber, controls]);

  return (
    <span id={id} ref={ref}>
      {numberHeight !== 0 && (
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            overflow: 'hidden'
          }}
          className={className}
        >
          {animateToNumbersArr.map((n, index) => {
            if (typeof n === 'string') {
              return (
                <div key={id + index} style={{ ...fontStyle, fontVariantNumeric: 'tabular-nums' }}>
                  {n}
                </div>
              );
            }

            return (
              <motion.div
                key={`${id}_${index}`}
                style={{
                  height: numberHeight,
                  width: numberWidth
                }}
                initial="hidden"
                variants={{
                  hidden: { y: 0 },
                  visible: {
                    y:
                      -1 *
                        (numberHeight *
                          (typeof animateToNumbersArr[index] === 'number'
                            ? (animateToNumbersArr[index] as number)
                            : 0)) -
                      numberHeight * 20
                  }
                }}
                animate={controls}
                transition={transitions?.(index)}
              >
                {NUMBERS.map((number, i) => (
                  <div key={id + number + i} style={{ ...fontStyle, fontVariantNumeric: 'tabular-nums' }}>
                    {number}
                  </div>
                ))}
              </motion.div>
            );
          })}
        </div>
      )}

      <div key={`${id}div`} ref={numberDivRef} style={{ position: 'absolute', top: -9999, ...fontStyle }}>
        {0}
      </div>
    </span>
  );
};

export const AvaniAnimatedNumber = memo(AnimatedNumber, (prevProps, nextProps) => {
  return (
    prevProps.animateToNumber === nextProps.animateToNumber &&
    prevProps.fontStyle === nextProps.fontStyle &&
    prevProps.includeComma === nextProps.includeComma
  );
});
